"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { Box, type BoxProps } from "../../components/ui/box";
import { Separator } from "../../components/ui/separator";
import { tabsTabVariants } from "../../components/ui/tabs";
import { cn } from "../../lib/utils";
import {
  DataTableToolbar,
  DataTableToolbarActions,
} from "../data-table/data-table-toolbar";

type ViewTabsPartProps = { children: React.ReactNode };
type ViewTabsContentProps = ViewTabsPartProps & { id?: string };
type ViewTabsRailPortalProps = ViewTabsPartProps & {
  /** Content to render when no ViewTabsFrame owns a rail-action host. */
  fallback?: React.ReactNode;
};
export type ViewTabsFrameProps = ViewTabsPartProps;

/** The strip genre a rail control sits beside. A `ViewTabsFrame` seats its
 *  rail on the pill strip; outside any frame the control assumes the
 *  page-level underline strip and dresses to its metric. */
type ViewTabsRailVariant = "pill" | "underline";

const ViewTabsVariantContext =
  React.createContext<ViewTabsRailVariant>("underline");
const ViewTabsRailPortalContext = React.createContext<{
  host: HTMLDivElement | null;
  setHost: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
} | null>(null);

/**
 * The page-level frame for a view rail and its content. The frame inherits
 * one continuous host surface and seats its rail controls on the pill strip.
 */
export function ViewTabsFrame({
  children,
}: ViewTabsFrameProps): React.ReactElement {
  const [railPortalHost, setRailPortalHost] =
    React.useState<HTMLDivElement | null>(null);
  const railPortal = React.useMemo(
    () => ({ host: railPortalHost, setHost: setRailPortalHost }),
    [railPortalHost],
  );

  return (
    <Box
      data-slot="view-tabs-frame"
      direction="column"
      fill="flex"
      minHeight={0}
      minWidth={0}
      overflow="hidden"
    >
      <ViewTabsVariantContext.Provider value="pill">
        <ViewTabsRailPortalContext.Provider value={railPortal}>
          {children}
        </ViewTabsRailPortalContext.Provider>
      </ViewTabsVariantContext.Provider>
    </Box>
  );
}

/** The page header that holds page identity and the tab rail. */
export function ViewTabsHeader({
  children,
}: ViewTabsPartProps): React.ReactElement {
  return (
    <Box
      data-slot="view-tabs-header"
      direction="column"
      gap="sm"
      paddingBottom="sm"
      paddingTop="sm"
      paddingX="md"
      position="relative"
      shrink={false}
      zIndex={10}
    >
      {children}
    </Box>
  );
}

/**
 * The bottom-aligned header row for page tabs and optional trailing controls.
 * Match the list variant to the surrounding `ViewTabsFrame`.
 */
export function ViewTabsRail({
  children,
}: ViewTabsPartProps): React.ReactElement {
  return (
    <Box
      data-slot="view-tabs-rail"
      align="end"
      direction="row"
      gap="sm"
      justify="between"
      minWidth={0}
    >
      {children}
    </Box>
  );
}

/** The right-aligned host for controls supplied by the active view body. */
export function ViewTabsRailActions(): React.ReactElement {
  const portal = React.useContext(ViewTabsRailPortalContext);
  return (
    <Box
      ref={portal?.setHost}
      data-slot="view-tabs-rail-actions"
      align="center"
      alignSelf="center"
      direction="row"
      minWidth={0}
      shrink={false}
    />
  );
}

/**
 * Moves controls owned by the active view body into ViewTabsRailActions.
 * React portals preserve the body's data and provider contexts. A standalone
 * view can supply its normal toolbar through `fallback`.
 */
export function ViewTabsRailPortal({
  children,
  fallback = children,
}: ViewTabsRailPortalProps): React.ReactPortal | React.ReactNode {
  const portal = React.useContext(ViewTabsRailPortalContext);
  if (!portal) return fallback;
  if (!portal.host) return null;
  return createPortal(children, portal.host);
}

/**
 * The standard active-view control cluster. It moves into the tab rail when a
 * host exists and keeps the normal data-toolbar row for standalone views.
 */
export function ViewTabsRailToolbar({
  children,
}: ViewTabsPartProps): React.ReactElement {
  const actions = <DataTableToolbarActions>{children}</DataTableToolbarActions>;
  return (
    <ViewTabsRailPortal
      fallback={<DataTableToolbar>{actions}</DataTableToolbar>}
    >
      {actions}
    </ViewTabsRailPortal>
  );
}

/**
 * A trailing control seated in the rail beside the tab strip. It follows the
 * frame's metric while staying visually quieter than a selected tab.
 */
export function ViewTabsRailButton({
  active = false,
  className,
  ...props
}: Omit<BoxProps<"button">, "as"> & {
  /** Paint the control as standing in for the selected tab — an overflow
   *  menu currently holding the active view. A pill strip lends the tabs'
   *  selected surface; an underline strip lights the bright ink and the
   *  selected wash. */
  active?: boolean;
}): React.ReactElement {
  const variant = React.useContext(ViewTabsVariantContext);
  return (
    <Box
      as="button"
      data-active={active ? "" : undefined}
      data-slot="view-tabs-rail-button"
      direction="row"
      align="center"
      // Centered on the tabs' midline: an underline control is the strip's
      // own 40px; the pill strip's 36px holds the 28px rung.
      alignSelf="center"
      shrink={false}
      className={cn(
        variant === "pill" && tabsTabVariants({ variant: "pill" }),
        variant === "underline" &&
          cn(
            tabsTabVariants({ variant: "underline" }),
            // Standalone, there is no list to inherit the resting ink from,
            // and the native `disabled` attribute must dim like a Base UI
            // `data-disabled` tab would. There is also no list stacking
            // context (`TabsList` is `relative z-0`) to trap the recipe's
            // `before:-z-1` wash pseudo, so without `isolate` it escapes to
            // a distant ancestor context and paints BEHIND the page fill —
            // an invisible hover/selected wash.
            "isolate text-muted-foreground data-active:before:bg-wash-selected disabled:pointer-events-none disabled:opacity-64",
          ),
        "grow-0",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The hairline between a tab strip and a trailing rail control. 16px tall,
 * centered on the strip's midline.
 *
 * `tone="strong"` paints the louder `--border-strong` rung — for a hairline
 * that is a boundary the eye must find (a pinned tab against the draggable
 * set), rather than a quiet seam before a rail control.
 *
 * Decorative by construction: it is drawn INSIDE the tab strip, and a tablist
 * owns tabs and nothing else — a separator announced there splits one set of
 * tabs into two for a reader who has no such boundary to act on.
 */
export function ViewTabsRailSeparator({
  tone,
}: {
  tone?: "strong";
} = {}): React.ReactElement {
  return (
    <Separator
      aria-hidden
      className="h-4 self-center"
      data-slot="view-tabs-rail-separator"
      orientation="vertical"
      tone={tone}
    />
  );
}

/** The filling page content below the view rail. */
export function ViewTabsContent({
  children,
  id,
}: ViewTabsContentProps): React.ReactElement {
  return (
    <Box
      id={id}
      data-slot="view-tabs-content"
      direction="column"
      fill="flex"
      minHeight={0}
      minWidth={0}
      overflow="hidden"
    >
      {children}
    </Box>
  );
}
